import React, { useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export interface ToolbarSecondaryActionItem {
    key: string;
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
    disabled?: boolean;
    hidden?: boolean;
    testId?: string;
    title?: string;
}

export function filterVisibleSecondaryActions(
    items?: readonly ToolbarSecondaryActionItem[],
): ToolbarSecondaryActionItem[] {
    if (!items || items.length === 0) return [];
    return items.filter((item) => !item.hidden);
}

export function triggerSecondaryActionSafely(
    closeMenu: (open: boolean) => void,
    action: () => void,
    scheduleFrame: (cb: () => void) => void =
        typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
            ? (cb) => {
                  window.requestAnimationFrame(cb);
              }
            : (cb) => {
                  setTimeout(cb, 0);
              },
): void {
    closeMenu(false);
    scheduleFrame(() => {
        action();
    });
}

export interface ToolbarSecondaryActionsProps {
    items?: readonly ToolbarSecondaryActionItem[];
    buttonLabel?: string;
    className?: string;
}

/**
 * ToolbarSecondaryActions — Chuẩn EMS gom nút phụ trên thanh công cụ danh sách (Rule 3.2.3):
 * - Desktop (`>= 640px`): Hiển thị 1 nút gọn `[··· Khác]` (`h-7`), đứng trước nút chính `+ Thêm/Tạo/Lập` (`addLast === true`).
 *   Đóng menu trước rồi thực thi `onClick` ở frame kế tiếp để không đóng sập Modal / Confirm (`FB-2HTJLR`).
 * - Mobile (`< 640px`): Render nút cầu nối mang `data-toolbar-secondary="true"` để `useMobileOverflow` gom chung vào
 *   duy nhất 1 menu `…` (`MobileOverflowMenu`), triệt tiêu hoàn toàn hiện tượng 2 nút `…` cạnh nhau.
 */
export function ToolbarSecondaryActions({
    items,
    buttonLabel = 'Khác',
    className,
}: ToolbarSecondaryActionsProps) {
    const [open, setOpen] = useState(false);
    const visibleItems = filterVisibleSecondaryActions(items);
    if (visibleItems.length === 0) return null;

    return (
        <React.Fragment>
            <DropdownMenu open={open} onOpenChange={setOpen}>
                <DropdownMenuTrigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        aria-label={buttonLabel}
                        data-testid="toolbar-secondary-menu-btn"
                        title="Thao tác bổ sung"
                        className={cn(
                            'hidden sm:inline-flex h-7 px-2 text-xs gap-1 font-medium text-slate-700 border-slate-200 bg-white hover:bg-slate-50 shrink-0 cursor-pointer',
                            className,
                        )}
                    >
                        <MoreHorizontal className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                        <span className="hidden min-[1440px]:inline">{buttonLabel}</span>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="end"
                    className="w-52 p-1 shadow-lg border-slate-200"
                    onCloseAutoFocus={(e) => e.preventDefault()}
                >
                    {visibleItems.map((item) => (
                        <DropdownMenuItem
                            key={item.key}
                            disabled={item.disabled}
                            data-testid={item.testId || `toolbar-secondary-item-${item.key}`}
                            title={item.title || item.label}
                            className="text-xs gap-2 py-1.5 px-2.5 cursor-pointer rounded-md font-medium text-slate-700 focus:bg-slate-100"
                            onSelect={(e) => {
                                e.preventDefault();
                                if (item.disabled) return;
                                triggerSecondaryActionSafely(setOpen, item.onClick);
                            }}
                        >
                            {item.icon && <span className="shrink-0 text-slate-500">{item.icon}</span>}
                            <span className="truncate">{item.label}</span>
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>

            {visibleItems.map((item) => (
                <button
                    key={`mobile-${item.key}`}
                    type="button"
                    data-toolbar-secondary="true"
                    disabled={item.disabled}
                    data-testid={item.testId ? `${item.testId}-mobile` : undefined}
                    title={item.title || item.label}
                    onClick={item.onClick}
                    className="sm:hidden inline-flex items-center justify-center gap-1 h-7 px-2 text-[11px] font-medium rounded-md border border-slate-200 bg-white text-slate-700 shrink-0 disabled:opacity-50"
                >
                    {item.icon}
                    <span>{item.label}</span>
                </button>
            ))}
        </React.Fragment>
    );
}
